class HvMobileMenuDrawer extends HTMLElement {
  static focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  connectedCallback() {
    this.toggleWrapper = this.closest('.mobile-toggle-wrapper');
    this.toggle = this.toggleWrapper?.querySelector(':scope > summary.mobile-toggle');
    this.isOpen = false;
    this.didFocus = false;
    this.onKeydown = null;

    if (!this.toggleWrapper || !this.toggle) return;

    this.observer = new MutationObserver(() => this.syncToggleState());
    this.observer.observe(this.toggleWrapper, {
      attributes: true,
      attributeFilter: ['class', 'open'],
    });

    this.onDetailsToggle = (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.open || !details.classList.contains('link-container')) return;
      if (!this.contains(details)) return;
      this.loadDeferredMedia(details);
    };
    this.addEventListener('toggle', this.onDetailsToggle, true);

    this.syncToggleState();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.onDetailsToggle) {
      this.removeEventListener('toggle', this.onDetailsToggle, true);
    }
    if (this.onKeydown) {
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  isDrawerOpen() {
    return this.toggleWrapper.classList.contains('active') || this.toggleWrapper.hasAttribute('open');
  }

  isDrawerReady() {
    if (!document.documentElement.classList.contains('js')) {
      return this.toggleWrapper.hasAttribute('open');
    }
    return this.toggleWrapper.classList.contains('active');
  }

  syncToggleState() {
    const isOpen = this.isDrawerOpen();

    if (!isOpen) {
      if (this.isOpen) {
        this.closeDrawer();
      }
      this.isOpen = false;
      this.didFocus = false;
      return;
    }

    if (!this.isOpen) {
      this.isOpen = true;
      this.openDrawer();
    }

    if (this.isDrawerReady() && !this.didFocus) {
      this.didFocus = true;
      requestAnimationFrame(() => {
        if (this.isDrawerOpen()) {
          this.focus();
        }
      });
    }
  }

  getFocusableElements(container) {
    if (!container) return [];

    return Array.from(container.querySelectorAll(HvMobileMenuDrawer.focusableSelector)).filter((element) => {
      if (element.closest('[hidden]')) return false;

      const closedDetails = element.closest('details:not([open])');
      if (closedDetails && !closedDetails.querySelector('summary')?.contains(element)) {
        return false;
      }

      return element.getClientRects().length > 0;
    });
  }

  trapFocus(event) {
    const focusable = [
      ...this.getFocusableElements(this.toggleWrapper),
      ...this.getFocusableElements(this),
    ].filter((element, index, elements) => elements.indexOf(element) === index);

    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  activatePicture(picture) {
    if (!picture || picture.dataset.mediaLoaded === 'true') return;
    picture.dataset.mediaLoaded = 'true';
    picture.querySelectorAll('[data-src], [data-srcset]').forEach((element) => {
      if (element.dataset.srcset && !element.getAttribute('srcset')) element.srcset = element.dataset.srcset;
      if (element.dataset.src && !element.getAttribute('src')) element.src = element.dataset.src;
      element.removeAttribute('loading');
    });
  }

  loadDeferredMedia(root) {
    const scope = root || this;
    scope.querySelectorAll('picture[data-deferred]').forEach((picture) => {
      const menuItem = picture.closest('details.link-container');
      if (!root && menuItem) return;
      if (menuItem && !menuItem.open) return;
      this.activatePicture(picture);
    });
  }

  openDrawer() {
    this.toggle.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.closeLabel || 'Close menu');
    this.loadDeferredMedia();
    this.onKeydown = (event) => {
      if (event.key === 'Escape') this.toggle.click();
      if (event.key === 'Tab') this.trapFocus(event);
    };
    document.addEventListener('keydown', this.onKeydown);
  }

  closeDrawer() {
    if (this.onKeydown) {
      document.removeEventListener('keydown', this.onKeydown);
      this.onKeydown = null;
    }

    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-label', this.toggle.dataset.openLabel || 'Open menu');
    document.body.classList.remove('overflow-hidden');
    this.toggle.focus();
  }
}

if (!customElements.get('hv-mobile-menu-drawer')) {
  customElements.define('hv-mobile-menu-drawer', HvMobileMenuDrawer);
}
